import { filter, chunk } from 'lodash';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import { useState, useEffect, View } from 'react';
import plusFill from '@iconify/icons-eva/plus-fill';
import backFill from '@iconify/icons-eva/arrow-ios-back-fill';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Modal,
  Box,
  Backdrop,
  Paper
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';

// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserListToolbar, UserMoreMenu } from '../../components/user';
import UserModal from './PartnerUserModal';
import ConfDialog from '../../components/ConfDialog';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'user_id', label: 'ユーザーID', alignRight: false },
  { id: 'name', label: '名前', alignRight: false },
  { id: 'position', label: '役職', alignRight: false },
  { id: 'role', label: '権限', alignRight: false },
  { id: '' }
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, role) {
  if (query) {
    array = filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  if (role !== -1) {
    array = filter(array, (_target) => _target.role === role);
  }

  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function PartnerUser({
  open = false,
  partnerId = 0,
  partnerCode = 0,
  partnerName = '',
  onClose,
  updatePartnerDt
}) {
  const { SystemLib } = window;
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [modal, setModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentTargetId, setCurrentTargetId] = useState(0);
  const [updateDt, setUpdateDt] = useState(moment().unix());

  const [isLoading, setIsLoading] = useState(false);
  const [conf, setConf] = useState(false);
  const [confDelAll, setConfDelAll] = useState(false);

  const [role, setRole] = useState(-1);

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    getUsers();
  }, [partnerCode, updatePartnerDt]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredUsers.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

  const filteredUsers = applySortFilter(users, getComparator(order, orderBy), filterName, role);

  const isUserNotFound = filteredUsers.length === 0;

  const useStyles = makeStyles({
    tableCell: {
      height: 10
    }
  });

  const getUsers = async () => {
    setIsLoading(true);
    const { data } = await SystemLib.api(
      'partner/user/list',
      {
        partner_code: partnerCode
      },
      'post'
    );
    if (data.result) {
      setUsers(data.data);
    }
    setIsLoading(false);
  };

  const getUser = async (id) => {
    const { data } = await SystemLib.api(`partner/user/${id}`, {}, 'get');
    if (data.result) {
      let flag = false;
      if (users) {
        const arr = users.slice();
        arr.forEach((v, i) => {
          if (v.id === id) {
            arr[i] = data.data;
            setUsers(arr);
            flag = true;
          }
        });
      }
      if (!flag) {
        setOrderBy(null);
        const arr = users.slice();
        arr.unshift(data.data);
        setUsers(arr);
      }
    }
  };

  const deleteUsers = async (ids) => {
    const tmp = chunk(ids, 1000);
    const p = [];
    tmp.forEach(async (arr) => {
      console.log(arr);
      p.push(
        SystemLib.api(
          `partner/user/delete`,
          {
            ids: arr
          },
          'post'
        )
      );
    });
    await Promise.all(p);
    const newUsers = users.filter((v) => !ids.includes(v.id));
    setUsers(newUsers);
  };

  const reload = async (id) => {
    getUser(id);
  };

  const classes = useStyles();

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        background: '#fff',
        height: '100%',
        width: '100%',
        display: open ? 'block' : 'none'
      }}
    >
      <Page title="パートナー：ユーザー管理">
        <Container sx={{ position: 'relative' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4" gutterBottom display="block">
              {partnerName}：ユーザー管理
            </Typography>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Icon icon={backFill} />}
                onClick={() => {
                  onClose(partnerId);
                }}
              >
                戻る
              </Button>
              <Button
                variant="contained"
                startIcon={<Icon icon={plusFill} />}
                onClick={() => {
                  setCurrentTargetId(0);
                  setUpdateDt(moment().unix());
                  setModal(true);
                }}
              >
                新規登録
              </Button>
            </Stack>
          </Stack>

          <Card>
            <UserListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
              onDelete={() => {
                setConfDelAll(true);
              }}
              onChangeRole={(v) => {
                setRole(v);
              }}
              useMaster={false}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={filteredUsers.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const { id, user_id, name_f, name_l, name, role, position } = row;
                        const isItemSelected = selected.indexOf(id) !== -1;
                        let role_str = '';
                        switch (role) {
                          case 'partner_admin':
                            role_str = '管理者';
                            break;
                          case 'partner_user':
                            role_str = '一般ユーザー';
                            break;
                          default:
                            role_str = '';
                            break;
                        }

                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                onChange={(event) => handleClick(event, id)}
                              />
                            </TableCell>
                            <TableCell component="th" scope="row" padding="none" width={200}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Typography variant="subtitle2" noWrap>
                                  {user_id}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              padding="none"
                              sx={{ minWidth: 200 }}
                            >
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Typography variant="subtitle2" noWrap>
                                  {name}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell scope="row" padding="none">
                              <Stack direction="row" alignItems="center" spacing={2}>
                                {position}
                              </Stack>
                            </TableCell>
                            <TableCell align="left" padding="none">
                              {role_str}
                            </TableCell>
                            <TableCell align="right" padding="none">
                              <UserMoreMenu
                                itemId={id}
                                onEdit={(id) => {
                                  setCurrentTargetId(id);
                                  setUpdateDt(moment().unix());
                                  setModal(true);
                                }}
                                onDelete={(id) => {
                                  setCurrentTargetId(id);
                                  setConf(true);
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isUserNotFound && !isLoading && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                  {isUserNotFound && isLoading && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <Paper>
                            <Typography gutterBottom align="center" variant="subtitle1">
                              読み込み中
                            </Typography>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="表示件数"
            />
          </Card>
        </Container>
        {modal && (
          <UserModal
            targetGroup={partnerCode}
            targetId={currentTargetId}
            open={modal}
            onClose={() => {
              setModal(false);
            }}
            updateDt={updateDt}
            reload={(id) => {
              reload(id);
            }}
          />
        )}
        <ConfDialog
          open={conf}
          onOk={() => {
            deleteUsers([currentTargetId]);
            setCurrentTargetId(0);
            setConf(false);
          }}
          onCancel={() => {
            setConf(false);
          }}
          onClose={() => {
            setConf(false);
          }}
        >
          削除してもよろしいでしょうか？
        </ConfDialog>
        <ConfDialog
          open={confDelAll}
          onOk={() => {
            deleteUsers(selected);
            setSelected([]);
            setConfDelAll(false);
          }}
          onCancel={() => {
            setConfDelAll(false);
          }}
          onClose={() => {
            setConfDelAll(false);
          }}
        >
          削除してもよろしいでしょうか？ ({selected.length}件)
        </ConfDialog>
      </Page>
    </div>
  );
}
